import * as L from 'leaflet';
import { GestureHandling } from "leaflet-gesture-handling";
import { isNullOrWhitespace } from './helpers';
import { ITriggerZone, Circle, GpsCoordinates } from "./sharedModels";
import { randomColor } from "randomcolor";
import { IGpsCoordinates } from './_generated/IGpsCoordinates';
import { TriggerZoneType } from './_generated/TriggerZoneType';

export function initializeMap(
    coordinates: L.LatLng[]
): L.Map {
    const boundSizeInMeters = 200;
    var bounds = L.latLngBounds(coordinates);
    if (coordinates.length === 1) {
        bounds = coordinates[0].toBounds(boundSizeInMeters);
    } else if (coordinates.length === 2) {
        if (coordinates[0].distanceTo(coordinates[1]) < boundSizeInMeters) {
            bounds = coordinates[0].toBounds(boundSizeInMeters);
        }
    }

    L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);
    const map = L.map("map", { fullscreenControl: true }).fitBounds(bounds, { padding: [50, 50] });

    // force leaflet to redraw to handle the case when map initially hidden which causes wrong initial map render
    const observer = new IntersectionObserver((entries) =>
        {
            entries.forEach(entry => {
                if (entry.intersectionRatio > 0) {
                    map.invalidateSize();
                    map.fitBounds(bounds, { padding: [50, 50] });
                    observer.disconnect();
                }
            });
        },
        {
            root: document.documentElement,
        });
    observer.observe(document.getElementById('map')!);

    // @ts-ignore
    map.gestureHandling.enable();

    L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
        {
            maxZoom: 22,
            id: "mapbox/outdoors-v11",
            accessToken: "pk.eyJ1Ijoic21hcnQtZ3VpZGUiLCJhIjoiY2s0c2dyaWR4MDA0eDNrbW00czFwczc4cyJ9.2FnIFSxmPsJkD-o5NLOUPA",
            tileSize: 512,
            zoomOffset: -1
        }).addTo(map);

    L.Icon.Default.imagePath = "/lib/leaflet/images/";

    return map;
}

export function addBoundingBoxToMap(
    map: L.Map,
    northWestLatitude: number,
    northWestLongitude: number,
    southEastLatitude: number,
    southEastLongitude: number,
    color: string
) {
    const nwCorner = L.latLng(northWestLatitude, northWestLongitude);
    const seCorner = L.latLng(southEastLatitude, southEastLongitude);

    L.polygon(
        ([
            [nwCorner.lat, nwCorner.lng],
            [nwCorner.lat, seCorner.lng],
            [seCorner.lat, seCorner.lng],
            [seCorner.lat, nwCorner.lng]
        ]) as any,
        {
            fill: false,
            color: color
        }).addTo(map);
}

export function addPoiToMap(
    map: L.Map,
    poiCoordinates: L.LatLngExpression,
    poiMainImageUrl?: string,
    poiName?: string,
    markerOptions?: L.MarkerOptions
) : L.Marker<any> {
    const marker = L.marker(poiCoordinates, markerOptions);

    if (!isNullOrWhitespace(poiName)) {
        const imageTag = !isNullOrWhitespace(poiMainImageUrl)
            ? `<img src="${poiMainImageUrl}" style="width: 100%; height: 5vw; object-fit:contain" />`
            : "";
        const popupContent = `<p>${poiName}</p>${imageTag}`;
        marker.bindPopup(popupContent);
    }

    marker.addTo(map);
    return marker;
}

export function addTourToMap(
    map: L.Map,
    waypoints: L.LatLngExpression[],
    firstWaypoint: L.LatLngExpression,
    lastWaypoint: L.LatLngExpression,
    tourMainImageUrl: string,
    tourName: string,
    color: string
) {
    const tourStartIcon = L.divIcon({ className: "tour-start-icon", html: '<i class="fas fa-walking" style="font-size: 2rem"></i>' });
    const tourFinishIcon = L.divIcon({ className: "tour-finish-icon", html: '<i class="fas fa-flag-checkered" style="font-size: 1.5rem"></i>' });
    const tourPopupContent = `<p>${tourName}</p><img src="${tourMainImageUrl}" style="width: 100%; height: 5vw; object-fit:contain"/>`;
    L.polyline(waypoints, { color: color, weight: 5 }).bindPopup(tourPopupContent).addTo(map);
    L.marker(firstWaypoint, { icon: tourStartIcon }).bindPopup(tourPopupContent).addTo(map);
    L.marker(lastWaypoint, { icon: tourFinishIcon }).bindPopup(tourPopupContent).addTo(map);
}

export function addTriggerZonesToMap(
    map: L.Map,
    triggerZones: ITriggerZone[],
    poiName?: string
) {
    $.each(triggerZones, (index, triggerZone) => {
        var color: string = randomColor();

        if (triggerZone.type === TriggerZoneType.Circle) {

            if (!triggerZone.circleTypeGpsCoordinates || !triggerZone.circleTypeRadius) {
                throw new Error('invalid trigger zone');
            }

            const circleLatLng = new L.LatLng(
                triggerZone.circleTypeGpsCoordinates.latitude,
                triggerZone.circleTypeGpsCoordinates.longitude,
                triggerZone.circleTypeGpsCoordinates.altitude
            );
            L.circle(circleLatLng,
                {
                    radius: triggerZone.circleTypeRadius,
                    color: color
                })
                .bindPopup(`<p>Trigger zone circle${!isNullOrWhitespace(poiName) ? ` for Place ${poiName}` : ""}, radius ${Math.round(triggerZone.circleTypeRadius)} m</p>`)
                .addTo(map);

        } else if (triggerZone.type === TriggerZoneType.Polygon) {
            if (!triggerZone.polygonTypePolygonCorners) {
                throw new Error('invalid trigger zone');
            }

            const triggerZoneLatLngs = triggerZone.polygonTypePolygonCorners
                .map(x => new L.LatLng(x.latitude, x.longitude, x.altitude));
            L.polyline(triggerZoneLatLngs.concat(triggerZoneLatLngs[0]), { color: color, weight: 5, fill: true })
                .bindPopup(`<p>Trigger zone polygon${!isNullOrWhitespace(poiName) ? ` for Place ${poiName}` : ""}</p>`).addTo(map);

        } else {
            throw new Error(`Unsupported trigger zone type ${triggerZone.type}`);
        }
    });
}

type SetPolylineWaypointsCallback = (polylineWaypoints: GpsCoordinates[]) => void;
type SetPolygonsAndCirclesCallback = (polygons: GpsCoordinates[][], circles: Circle[]) => void;

const polylineLayerTypeName: string = "polyline";
const polygonLayerTypeName: string = "polygon";
const circleLayerTypeName: string = "circle";

export class PolylineInfo {
    drawnItems: L.FeatureGroup;
    drawControlFull: L.Control.Draw;
    drawControlEditOnly: L.Control.Draw;

    constructor(
        drawnItems: L.FeatureGroup,
        drawControlFull: L.Control.Draw,
        drawControlEditOnly: L.Control.Draw
    ) {
        this.drawnItems = drawnItems;
        this.drawControlFull = drawControlFull;
        this.drawControlEditOnly = drawControlEditOnly;
    }
}

export function allowDrawingOnePolyline(
    map: L.Map,
    color: string,
    polylineWaypoints: IGpsCoordinates[],
    setPolylineWaypointsCallback: SetPolylineWaypointsCallback,
    customLabels?: [string, string][]
): PolylineInfo {
    
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControlFull = new L.Control.Draw({
        edit: {
            featureGroup: drawnItems
        },
        draw: {
            polygon: false,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false
        }
    });

    const drawControlEditOnly = new L.Control.Draw({
        edit: {
            featureGroup: drawnItems
        },
        draw: {
            polyline: false,
            polygon: false,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false
        }
    });

    if (polylineWaypoints.length > 0) {
        drawnItems.addLayer(L.polyline(polylineWaypoints.map(x => new L.LatLng(x.latitude, x.longitude, x.altitude)), { color: color, weight: 5 }));
        map.addControl(drawControlEditOnly);
    } else {
        map.addControl(drawControlFull);
    }

    map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItems.addLayer(e.layer);
        refreshPolylineWaypoints(e.layer, setPolylineWaypointsCallback);

        map.removeControl(drawControlFull);
        map.addControl(drawControlEditOnly);
        applyCustomLabels();
    });

    map.on(L.Draw.Event.EDITED, (e: any) => {
        const layers: L.Layer[] = [];
        e.layers.eachLayer(layer => {
            layers.push(layer);
        });
        if (layers.length === 0) return;
        refreshPolylineWaypoints(layers[0], setPolylineWaypointsCallback);
        applyCustomLabels();
    });

    map.on(L.Draw.Event.DELETED, (e: any) => {
        var layersCount = 0;
        e.layers.eachLayer(() => {
            layersCount++;
        });
        if (layersCount === 0) return;
        refreshPolylineWaypoints(undefined, setPolylineWaypointsCallback);

        if (drawnItems.getLayers().length === 0) {
            map.removeControl(drawControlEditOnly);
            map.addControl(drawControlFull);
        }
        applyCustomLabels();
    });

    applyCustomLabels();

    return new PolylineInfo(drawnItems, drawControlFull, drawControlEditOnly);

    function applyCustomLabels() {
        if (customLabels) {
            customLabels.forEach(labelByCssClassSelector => {
                $(`${labelByCssClassSelector[0]}`).prop("title", labelByCssClassSelector[1]);
            });
        }
    }
}

export function updatePolyline(
    map: L.Map,
    color: string,
    polylineWaypoints: IGpsCoordinates[],
    polylineInfo: PolylineInfo,
)  {
    var layers: L.Layer[] = [];
    polylineInfo.drawnItems.eachLayer(layer => {
        layers.push(layer);
    });
    if (layers.length > 0) {
        polylineInfo.drawnItems.removeLayer(layers[0]);
    }
    polylineInfo.drawnItems.addLayer(L.polyline(polylineWaypoints.map(x => new L.LatLng(x.latitude, x.longitude, x.altitude)), { color: color, weight: 5 }));
    map.removeControl(polylineInfo.drawControlFull);
    map.addControl(polylineInfo.drawControlEditOnly);
}

function refreshPolylineWaypoints(
    polylineLayer: L.Layer | undefined,
    setPolylineWaypointsCallback: SetPolylineWaypointsCallback
) {
    const polylineWaypoints = polylineLayer
        ? (polylineLayer as any).getLatLngs().map(x => new GpsCoordinates(x.lat, x.lng))
        : new Array<GpsCoordinates>();
    setPolylineWaypointsCallback(polylineWaypoints);
}

export function allowDrawingPolygonsAndCircles(
    map: L.Map,
    color: string,
    polygons: IGpsCoordinates[][],
    circles: Circle[],
    setPolygonsAndCirclesCallback: SetPolygonsAndCirclesCallback,
    customLabels?: [string, string][]
) {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
        edit: {
            featureGroup: drawnItems
        },
        draw: {
            polyline: false,
            rectangle: false,
            marker: false,
            circlemarker: false
        }
    });

    map.addControl(drawControl);

    polygons.forEach(polygonCorners => {
        if (polygonCorners.length > 0) {
            drawnItems.addLayer(L.polygon(polygonCorners.map(x =>
                new L.LatLng(
                    x.latitude,
                    x.longitude,
                    x.altitude
                )
            ), { color: color, weight: 5 }));
        }
    });

    circles.forEach(circle => {
        drawnItems.addLayer(L.circle([circle.latitude, circle.longitude], circle.radius, { color: color, weight: 5 }));
    });

    map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItems.addLayer(e.layer);
        refreshPolygonsAndCircles(drawnItems.getLayers(), setPolygonsAndCirclesCallback);
        applyCustomLabels();
    });

    map.on(L.Draw.Event.EDITED, () => {
        refreshPolygonsAndCircles(drawnItems.getLayers(), setPolygonsAndCirclesCallback);
        applyCustomLabels();
    });

    map.on(L.Draw.Event.DELETED, () => {
        refreshPolygonsAndCircles(drawnItems.getLayers(), setPolygonsAndCirclesCallback);
        applyCustomLabels();
    });

    applyCustomLabels();

    function applyCustomLabels() {
        if (customLabels) {
            customLabels.forEach(labelByCssClassSelector => {
                $(`${labelByCssClassSelector[0]}`).prop("title", labelByCssClassSelector[1]);
            });
        }
    }
}

function refreshPolygonsAndCircles(
    layers: L.Layer[],
    setPolygonsAndCirclesCallback: SetPolygonsAndCirclesCallback
) {
    const polygonLayers: L.Layer[] = [];
    const circleLayers: L.Layer[] = [];

    layers.forEach(layer => {
        const layerType = getLayerType(layer);
        if (layerType === polygonLayerTypeName) {
            polygonLayers.push(layer);
        } else if (layerType === circleLayerTypeName) {
            circleLayers.push(layer);
        } else {
            throw new Error(`Unsupported layer type: ${layerType}`);
        }
    });

    var polygons = new Array<GpsCoordinates[]>();
    polygonLayers.forEach((polygonLayer: any) => {
        polygons.push(polygonLayer.getLatLngs()[0].map(x => new GpsCoordinates(x.lat, x.lng)));
    });
    var circles = new Array<Circle>();
    circleLayers.forEach((circleLayer: any) => {
        var circleLatLng = circleLayer.getLatLng();
        circles.push(new Circle(circleLatLng.lat, circleLatLng.lng, circleLayer.getRadius()));
    });
    setPolygonsAndCirclesCallback(polygons, circles);
}

function getLayerType(layer: L.Layer) {

    if (layer instanceof L.Rectangle) return "rectangle";
    if (layer instanceof L.Polygon) return polygonLayerTypeName;
    if (layer instanceof L.Polyline) return polylineLayerTypeName;
    if (layer instanceof L.Marker) return "marker";
    if (layer instanceof L.Circle) return circleLayerTypeName;

    throw new Error(`Unsupported layer type: ${layer}`);
}

